// src/hooks/useSessions.ts

import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  fetchSessions,
  sendRemoveRequest,
  confirmRemove,
  resendPinCode,
} from '@/store/sessions/thunks';
import { UUID } from '@/types';
import {
  askEndAll,
  askEndOne,
  cancel,
  resetErrors,
} from '@/store/sessions/slice.ts';
import { useAppDispatch, useAppSelector } from '@/store/store.ts';

export const useSessions = () => {
  const { current, others, loading, step, targetIds } = useAppSelector(
    (s) => s.sessions
  );
  const targetSession = useMemo(() => {
    return targetIds?.length === 1
      ? others.find((s) => s.id === targetIds[0])
      : undefined;
  }, [others, targetIds]);
  return { current, otherSessions: others, loading, step, targetSession };
};

export const useSessionActions = () => {
  const dispatch = useAppDispatch();

  /* stage-0: open confirm dialogs */
  const openConfirmOne = useCallback(
    (id: UUID) => dispatch(askEndOne(id)),
    [dispatch]
  );
  const openConfirmAll = useCallback(() => dispatch(askEndAll()), [dispatch]);
  const dismiss = useCallback(() => dispatch(cancel()), [dispatch]);

  /* stage-1: user hit YES */
  const requestSms = useCallback(
    () => dispatch(sendRemoveRequest()),
    [dispatch]
  );

  const refresh = useCallback(() => dispatch(fetchSessions()), [dispatch]);

  return {
    openConfirmOne,
    openConfirmAll,
    dismiss,
    requestSms,
    refresh,
  };
};
export const usePin = () => {
  const { step, pinError, loading, resendTime } = useAppSelector(
    (s) => s.sessions
  );
  const dispatch = useAppDispatch();
  const submitPin = useCallback(
    (code: string) => dispatch(confirmRemove({ code })),
    [dispatch]
  );
  const resendPin = useCallback(() => dispatch(resendPinCode()), [dispatch]);
  const resetError = useCallback(() => dispatch(resetErrors()), [dispatch]);
  const dismiss = useCallback(() => dispatch(cancel()), [dispatch]);

  return {
    step,
    error: pinError,
    submitPin,
    dismiss,
    loading,
    resendTime,
    resendPin,
    resetError,
  };
};

export const useInitSessions = () => {
  const dispatch = useAppDispatch();
  const hasFetched = useRef(false);

  useEffect(() => {
    if (!hasFetched.current) {
      void dispatch(fetchSessions());
      hasFetched.current = true; // guard against re-mounts in StrictMode
    }
  }, [dispatch]);
};
